import React from 'react';
import PropTypes from 'prop-types';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { cn } from '../../../../../lib/utils';

function DeleteReviewModal({
  isOpen,
  reasonValue,
  onReasonChange,
  onSave,
  onClose,
  onClear,
  isSaveDisabled,
  isSaveLoading,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative bg-background rounded-lg shadow-lg w-full max-w-md mx-4 p-6"
        style={{ border: '1px solid hsl(var(--border))' }}
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-lg bg-destructive/10">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Delete Review</h2>
            <p className="text-sm text-muted-foreground">This action cannot be undone</p>
          </div>
        </div>

        {/* Warning */}
        <div className="p-3 rounded-lg bg-destructive/5 border border-destructive/20 mb-4">
          <p className="text-sm text-destructive">
            Are you sure you want to delete this review? All progress will be reset.
          </p>
        </div>

        {/* Input */}
        <div className="space-y-2 mb-6">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label htmlFor="delete-reason" className="text-sm font-medium">
            Reason for deletion <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <textarea
              id="delete-reason"
              value={reasonValue}
              onChange={onReasonChange}
              placeholder="Please provide a reason for deleting this review..."
              rows={4}
              className={cn(
                'w-full px-3 py-2 rounded-md text-sm resize-none',
                'bg-background border border-input',
                'focus:outline-none focus:ring-2 focus:ring-destructive focus:border-transparent',
              )}
            />
            {reasonValue && (
              <button
                type="button"
                onClick={onClear}
                className="absolute right-3 top-3 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onSave} disabled={isSaveDisabled || isSaveLoading}>
            {isSaveLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Delete Review
          </Button>
        </div>
      </div>
    </div>
  );
}

DeleteReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  reasonValue: PropTypes.string,
  onReasonChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  isSaveDisabled: PropTypes.bool,
  isSaveLoading: PropTypes.bool,
};

DeleteReviewModal.defaultProps = {
  reasonValue: '',
  isSaveDisabled: false,
  isSaveLoading: false,
};

export default DeleteReviewModal;
